import { useState, useEffect, useContext } from "react";
import { apiGetImage } from "../sdk";
import { AuthContext } from "../contexts/AuthContext";

type CacheItem = {
  type: 'blob' | 'external';
  data: Blob | string;
  refCount: number;
};

const blobCache = new Map<string, CacheItem>();

/** 
 * Custom hook to fetch images with authentication
 * @param imageUrl Can be a full URL, a path, or an image ID
 * @param fallbackSrc Optional fallback image source
 * @returns Object URL for the image or fallback
 */
export function useImageWithAuth(imageUrl?: string, fallbackSrc?: string) {
  const [imageSrc, setImageSrc] = useState<string | undefined>(fallbackSrc);
  const { token } = useContext(AuthContext);

  useEffect(() => {
    if (!imageUrl) {
      setImageSrc(fallbackSrc);
      return;
    }

    let mounted = true;
    let localObjectUrl: string | null = null;

    if (blobCache.has(imageUrl)) {
      const cachedItem = blobCache.get(imageUrl)!;
      cachedItem.refCount++;

      if (cachedItem.type === 'external') {
        setImageSrc(cachedItem.data as string);
      } else {
        const url = URL.createObjectURL(cachedItem.data as Blob);
        localObjectUrl = url;
        setImageSrc(url);
      }
      return;
    }

    // Handle external and API URLs
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      try {
        const baseUrl = "pentra.ai";
        console.log('testing the images: ', baseUrl)
        if (!imageUrl.includes(baseUrl)) {
          setImageSrc(imageUrl);
          blobCache.set(imageUrl, {
            type: 'external',
            data: imageUrl,
            refCount: 1
          });
          return;
        }

        // const path = imageUrl.split(baseUrl)[1];
        fetchImage(imageUrl, imageUrl);
      } catch (e) {
        console.error('Error processing URL:', imageUrl, e);
        setImageSrc(fallbackSrc);
      }
    } else {
      fetchImage(imageUrl, imageUrl);
    }

    async function fetchImage(fullUrl: string, cacheKey: string) {
      if (!token) {
        setImageSrc(fallbackSrc);
        return;
      }

      try {
        console.log('Loading image with fullUrl:', fullUrl);
        const response = await apiGetImage(token, fullUrl);

        blobCache.set(cacheKey, {
          type: 'blob',
          data: response.data,
          refCount: 1
        });

        if (mounted) {
          const url = URL.createObjectURL(response.data);
          localObjectUrl = url;
          setImageSrc(url);
        }
      } catch (error) {
        console.error(`Failed to load image from ${fullUrl}:`, error);
        if (mounted) {
          setImageSrc(fallbackSrc);
        }
      }
    }

    return () => {
      mounted = false;

      // Decrease reference count in cache
      if (imageUrl && blobCache.has(imageUrl)) {
        const item = blobCache.get(imageUrl)!;
        item.refCount--;

        // If no more references, remove from cache
        if (item.refCount <= 0) {
          blobCache.delete(imageUrl);
        }
      }

      // Revoke object URL
      if (localObjectUrl) {
        URL.revokeObjectURL(localObjectUrl);
      }
    };
  }, [token, imageUrl, fallbackSrc]);

  return imageSrc;
}